import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildOrderReceiptPdf, type OrderReceiptPdfData } from './orderReceiptPdf';
import styles from '../../pages/Orders/Orders.module.css';

type OrderReceiptPdfPreviewProps = OrderReceiptPdfData & {
  autoPrint?: boolean;
};

const OrderReceiptPdfPreview: React.FC<OrderReceiptPdfPreviewProps> = ({
  protocolNumber,
  reporterName,
  address,
  neighborhood,
  houseNumberInFront,
  referencePoint,
  notes,
  lampCondition,
  priority,
  status,
  autoPrint = false,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const autoPrintedUrlRef = useRef<string | null>(null);

  const issuedAt = useMemo(() => new Date(), []);

  useEffect(() => {
    let cancelled = false;
    let localBlobUrl: string | null = null;

    (async () => {
      const document = await buildOrderReceiptPdf(
        {
          protocolNumber,
          reporterName,
          address,
          neighborhood,
          houseNumberInFront,
          referencePoint,
          notes,
          lampCondition,
          priority,
          status,
        },
        issuedAt,
      );

      if (cancelled) {
        return;
      }

      const blobUrl = URL.createObjectURL(document.output('blob'));
      localBlobUrl = blobUrl;
      setPdfUrl((currentUrl) => {
        if (currentUrl) {
          URL.revokeObjectURL(currentUrl);
        }
        return blobUrl;
      });
    })();

    return () => {
      cancelled = true;
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl);
      }
    };
  }, [
    address,
    houseNumberInFront,
    issuedAt,
    lampCondition,
    neighborhood,
    notes,
    priority,
    protocolNumber,
    referencePoint,
    reporterName,
    status,
  ]);

  const handlePrint = () => {
    const printWindow = iframeRef.current?.contentWindow;
    if (!printWindow) {
      return;
    }

    printWindow.focus();
    printWindow.print();
  };

  useEffect(() => {
    if (!autoPrint || !pdfUrl) {
      return;
    }

    autoPrintedUrlRef.current = null;
  }, [autoPrint, pdfUrl]);

  return (
    <div className={styles.receiptPreviewSection}>
      <p className={styles.locationHint}>
        Pre-visualizacao do comprovante em PDF entregue ao solicitante. O mapa nao e incluido neste documento.
      </p>

      <div className={styles.receiptPreviewToolbar}>
        {pdfUrl && (
          <>
            <button
              type="button"
              className={styles.receiptPrintButton}
              onClick={handlePrint}
            >
              Imprimir PDF
            </button>
            <a
              className={styles.receiptDownloadButton}
              href={pdfUrl}
              download={`comprovante-ordem-${protocolNumber || 'previa'}.pdf`}
            >
              Baixar PDF
            </a>
          </>
        )}
      </div>

      {pdfUrl ? (
        <iframe
          ref={iframeRef}
          title="Pre-visualizacao do comprovante em PDF"
          src={pdfUrl}
          className={styles.receiptPreviewFrame}
          onLoad={() => {
            if (!autoPrint || autoPrintedUrlRef.current === pdfUrl) {
              return;
            }

            autoPrintedUrlRef.current = pdfUrl;
            window.setTimeout(() => {
              handlePrint();
            }, 150);
          }}
        />
      ) : (
        <div className={styles.locationUnavailable}>Gerando PDF...</div>
      )}
    </div>
  );
};

export default OrderReceiptPdfPreview;